/**
 * The decisions behind the wallet connection window (app#315), kept free of wagmi and React so
 * they can be tested as plain functions: which way a tap on `Connect Wallet` goes, what the window
 * shows in each of its states, how a wallet app's link carries the pairing, and how a failure is
 * worded — in our words, never the library's.
 *
 * One rule, by device (Tim, 2026-11-02, spec §5). A phone whose browser has a wallet in it — the
 * wallet app's own browser — connects at once, as before; there is nothing to choose from. A phone
 * without one is offered the wallet apps — three opened through their universal links with the
 * pairing inside, Coinbase Wallet through Coinbase's own SDK (Tim, 2026-11-02: the one exception
 * to "everything our own", because the app publishes no link a pairing could be carried in) — and
 * behind them "All wallets": the Reown registry, with a search (§9 Р1). A computer always gets the
 * window: the extensions that announced themselves, each a row, and a row for the wallet on the
 * person's phone — a QR code; with no extension at all the window opens on the code straight
 * away. The person decides, the storefront only detects.
 */

import type { Platform, StoreLinks, RegistryWallet } from './walletRegistry';

import { isUserRejection, WalletConnectionCancelledError } from 'src/utils/walletErrors';

import {
  walletLink,
  storeLinkOf,
  filterByName,
  pairingLinkOf,
  hasPairingLink,
} from './walletRegistry';

/** What the window needs to know about a wagmi connector, structurally — no wagmi import here. */
export interface ConnectorIdentity {
  id: string;
  type?: string;
}

/**
 * wagmi's WalletConnect connector: a pairing with a wallet app over the relay, shown as a QR code
 * or carried in a wallet's link. It is never a browser wallet to be asked silently — the signing
 * walk (`rankLiveConnectors`) and the direct connection on a phone leave it out.
 */
export const isWalletConnectConnector = (connector: ConnectorIdentity): boolean =>
  connector.type === 'walletConnect' || connector.id === 'walletConnect';

/**
 * wagmi's Coinbase Wallet connector — Coinbase's SDK, which reaches the app over Coinbase's own
 * relay. Like the WalletConnect one it always has a provider (one it builds itself), so it too is
 * asked only from its row in the window, never found as a browser wallet.
 */
export const isCoinbaseSdkConnector = (connector: ConnectorIdentity): boolean =>
  connector.type === 'coinbaseWallet' || connector.id === 'coinbaseWalletSDK';

/** A connector the window asks from a row of its own — not a browser wallet to be found and asked silently. */
export const isWindowOnlyConnector = (connector: ConnectorIdentity): boolean =>
  isWalletConnectConnector(connector) || isCoinbaseSdkConnector(connector);

export type WindowLayout = 'desktop' | 'phone';

export type ConnectionPlan<T> =
  { kind: 'direct'; connector: T } | { kind: 'window'; layout: WindowLayout };

/**
 * Where a tap on `Connect Wallet` goes. `detected` are the browser wallets reachable right now, in
 * the order to ask them (the remembered one first) — the window-only connectors are not among them.
 */
export const planConnection = <T>(isMobile: boolean, detected: readonly T[]): ConnectionPlan<T> => {
  if (isMobile && detected.length > 0) {
    return { kind: 'direct', connector: detected[0] };
  }

  return { kind: 'window', layout: isMobile ? 'phone' : 'desktop' };
};

/** How long the window waits for the relay to hand over a pairing before it says it could not. */
export const URI_TIMEOUT_MS = 20_000;

// The window's words (spec §5). English: the app's i18n is an empty stub.
export const WINDOW_TITLE = 'Connect a wallet';
export const DETECTED_LABEL = 'Detected';
export const PHONE_ROW_TITLE = 'Wallet on your phone';
export const PHONE_ROW_HINT = 'Scan a QR code with your wallet app';
export const QR_HINT = 'Scan with your wallet app';
/** The accessible name of the code itself. */
export const QR_NAME = 'Connection QR code';
export const CONNECTING = 'Connecting…';
export const CLOSE_LABEL = 'Close';
export const TRY_AGAIN = 'Try again';
export const NO_BROWSER_WALLET =
  'No browser wallet found. Scan the code with the wallet app on your phone, or install a browser extension.';
export const RELAY_UNREACHABLE =
  'Could not reach WalletConnect. Check your connection and try again, or use a browser wallet.';
export const REQUEST_EXPIRED = 'The connection request expired. Try again.';

// The "All wallets" screen (spec §5, §9 Р1).
export const WALLETCONNECT_ROW_TITLE = 'WalletConnect';
export const WALLETCONNECT_ROW_HINT = 'All wallets';
export const ALL_WALLETS_TITLE = 'All wallets';
export const SEARCH_LABEL = 'Search wallet';
export const BACK_LABEL = 'Back';
export const LOADING_WALLETS = 'Loading wallets…';
export const REGISTRY_UNAVAILABLE = 'Could not load the wallet list. Try again.';
export const NO_WALLET_MATCH = 'No wallet matches your search.';
/** Under a wallet the registry lists no link for: the tap opens its store, not the app. */
export const INSTALL_HINT = 'Install';

export const waitingFor = (walletName: string): string =>
  `Waiting for ${walletName}… Open it if it did not come up`;

export const openAgain = (walletName: string): string => `Open ${walletName} again`;

/** A wallet reached through its own SDK could not be opened: a blocked pop-up, or no way through. */
export const popupBlocked = (walletName: string): string =>
  `Could not open ${walletName}. Allow pop-ups for this site and try again.`;

export const unreachable = (walletName: string): string =>
  `Could not reach ${walletName}. Check your connection and try again.`;

/**
 * A wallet asked through its SDK that never answered (app#389). Coinbase's page tells the SDK
 * nothing until the app has connected (`keys.coinbase.com`, read 2026-11-03): on a phone without
 * the app it leaves for the app's link and nobody answers; a page the person closed, or one that
 * closed after a refusal inside the app, ends the same way — one "request rejected" for all three.
 * The likeliest on a phone is the missing app, so the sentence says so and names the other
 * possibility in the same breath, never "cancelled" alone for a person who cancelled nothing.
 */
export const noAnswerFrom = (walletName: string): string =>
  `${walletName} did not answer — the app is probably not installed on this phone, or the request was cancelled.`;

/** The control beside `Try again` under that sentence: the app's store for this phone. */
export const installLabel = (walletName: string): string => `Install ${walletName}`;

export { walletLink } from './walletRegistry';

/**
 * Why a pairing did not end in a connection, for the person. A refusal in the wallet is the
 * existing cancelled-connection sentence; a pairing nobody answered before it ran out is said as
 * that; anything else — the relay down, the network gone, a provider that would not initialise —
 * is "could not reach WalletConnect", with the browser wallet named as the way round.
 */
export const connectionRefusal = (error: unknown): string => {
  if (isUserRejection(error)) {
    return new WalletConnectionCancelledError().message;
  }

  const text = typeof error === 'string' ? error : ((error as Error | undefined)?.message ?? '');

  if (/expired/i.test(text)) {
    return REQUEST_EXPIRED;
  }

  return RELAY_UNREACHABLE;
};

/** The way out beside `Try again`, when the refusal reads as a missing app: the app's store. */
export interface InstallLink {
  href: string;
  label: string;
}

/**
 * Why an attempt did not end in a connection, for the person — and the way out beyond `Try again`,
 * where there is one.
 */
export interface Refusal {
  message: string;
  install: InstallLink | null;
}

/**
 * The store the Coinbase row sends the person to after an attempt nobody answered: the registry's
 * links for the wallet's entry when "All wallets" has loaded it — the fresher source — else the
 * ones fixed beside the row (also when the registry's entry names no store at all); the platform's
 * store first, as the registry's grid picks it.
 */
export const installLinkOf = (
  wallet: SdkWallet,
  platform: Platform,
  registry: RegistryState
): InstallLink | null => {
  const listed =
    registry.status === 'ready'
      ? registry.wallets.find((entry) => entry.id === wallet.registryId)
      : undefined;
  const href =
    (listed ? storeLinkOf(listed, platform) : null) ??
    (wallet.stores ? storeLinkOf(wallet.stores, platform) : null);

  return href ? { href, label: installLabel(wallet.name) } : null;
};

/**
 * Why a wallet asked through its own SDK (Coinbase's) did not connect. The SDK's "rejected" — all
 * it says when its page went away without an answer, see `noAnswerFrom` — is the sentence that
 * names the missing app, with the store beside it; the SDK's page refused by the browser as a
 * pop-up is said as that, with the way round; anything else is "could not reach". Coinbase's own
 * words never reach the screen.
 */
export const sdkRefusal = (
  error: unknown,
  wallet: SdkWallet,
  platform: Platform,
  registry: RegistryState
): Refusal => {
  if (isUserRejection(error)) {
    return {
      message: noAnswerFrom(wallet.name),
      install: installLinkOf(wallet, platform, registry),
    };
  }

  const text = typeof error === 'string' ? error : ((error as Error | undefined)?.message ?? '');

  if (/popup/i.test(text)) {
    return { message: popupBlocked(wallet.name), install: null };
  }

  return { message: unreachable(wallet.name), install: null };
};

/** A browser wallet as the window lists it: wagmi's connector, named and drawn by the wallet itself. */
export interface DetectedWallet {
  id: string;
  name: string;
  /** The wallet's own icon, announced with it (EIP-6963 `info.icon`, a data URL). */
  icon?: string;
}

/** A wallet app the phone list offers as a link: its universal link opens it with the pairing. */
export interface LinkableWallet {
  kind: 'link';
  id: string;
  name: string;
  icon: string;
  universalLink: string;
  /** The same wallet's id in the Reown registry, so "All wallets" opens it through this link. */
  registryId?: string;
}

/** A wallet app the phone list offers through its own SDK — Coinbase Wallet (Tim, 2026-11-02). */
export interface SdkWallet {
  kind: 'sdk';
  id: string;
  name: string;
  icon: string;
  registryId?: string;
  /**
   * The app's stores and site, fixed beside the row: where an attempt nobody answered sends the
   * person (app#389).
   */
  stores?: StoreLinks;
}

/** A row of the phone list. */
export type PhoneRow = LinkableWallet | SdkWallet;

export type WindowStep = 'list' | 'qr' | 'waiting' | 'all';

/** What the registry has answered so far, as the window is told it. */
export type RegistryState =
  | { status: 'loading' }
  | { status: 'ready'; wallets: readonly RegistryWallet[] }
  | { status: 'failed' };

/** What the window is told, from which it works out what to show. */
export interface WindowState {
  layout: WindowLayout;
  step: WindowStep;
  detected: readonly DetectedWallet[];
  /** The wallet apps offered on a phone, in the order of the decision. */
  wallets: readonly PhoneRow[];
  /** Whether a pairing can be made at all (the WalletConnect connector is configured). */
  canPair: boolean;
  /** The pairing the relay handed over, once it has. */
  uri: string | null;
  /** The refusal, once there is one. */
  error: string | null;
  /** The way out offered beside it, when the refusal reads as a missing app: the app's store. */
  install: InstallLink | null;
  /** The wallet app the person tapped, on a phone. */
  chosen: PhoneRow | null;
  /** What is typed into the search of "All wallets". */
  query: string;
  /** The registry, as far as it has answered. */
  registry: RegistryState;
  /** The phone's platform: which store a wallet without a link is fetched from. */
  platform: Platform;
}

/** What a tap on a cell of "All wallets" does. */
export type RegistryAction =
  /** Opens the app with the pairing inside. */
  | { kind: 'link'; href: string }
  /** A link row before the relay handed the pairing over: the cell is there, it leads nowhere yet. */
  | { kind: 'pending' }
  /** The registry lists no link to open the app with: the store, or the wallet's site. */
  | { kind: 'store'; href: string }
  /** A wallet the phone list reaches through its SDK: the same, from here. */
  | { kind: 'sdk'; wallet: SdkWallet };

/** A wallet of the registry as the grid draws it. */
export interface RegistryRow {
  id: string;
  name: string;
  icon: string | null;
  action: RegistryAction;
}

/**
 * The rows of "All wallets" for the registry's wallets. A wallet the phone list already offers
 * opens the way the list opens it — the link fixed in `phoneWallets.ts`, or the SDK — one truth
 * for one wallet, whatever screen it is tapped on, and wears the official mark the list wears.
 * Any other opens through the link the registry gives it, formatted as Reown's window formats it;
 * a wallet without any link is a row that opens its store; a wallet the registry knows nothing
 * about is no row at all.
 */
export const registryRows = (
  wallets: readonly RegistryWallet[],
  offered: readonly PhoneRow[],
  uri: string | null,
  platform: Platform
): RegistryRow[] =>
  wallets.flatMap((wallet): RegistryRow[] => {
    const own = offered.find((row) => row.registryId === wallet.id);

    if (own?.kind === 'sdk') {
      return [
        { id: wallet.id, name: own.name, icon: own.icon, action: { kind: 'sdk', wallet: own } },
      ];
    }

    if (own) {
      return [
        {
          id: wallet.id,
          name: own.name,
          icon: own.icon,
          action: uri
            ? { kind: 'link', href: walletLink(own.universalLink, uri) }
            : { kind: 'pending' },
        },
      ];
    }

    if (hasPairingLink(wallet)) {
      return [
        {
          id: wallet.id,
          name: wallet.name,
          icon: wallet.icon,
          action: uri
            ? { kind: 'link', href: pairingLinkOf(wallet, uri) as string }
            : { kind: 'pending' },
        },
      ];
    }

    const store = storeLinkOf(wallet, platform);

    return store
      ? [
          {
            id: wallet.id,
            name: wallet.name,
            icon: wallet.icon,
            action: { kind: 'store', href: store },
          },
        ]
      : [];
  });

export type WindowView =
  | { kind: 'desktop'; detected: readonly DetectedWallet[]; phoneRow: boolean }
  | { kind: 'qr'; uri: string | null; noBrowserWallet: boolean }
  | {
      kind: 'phone';
      wallets: readonly PhoneRow[];
      uri: string | null;
      /** Whether the `WalletConnect` row — "All wallets" — is offered: only with a pairing to carry. */
      allWallets: boolean;
    }
  | {
      kind: 'all';
      query: string;
      /** `failed` covers an empty answer too: a registry with no wallets is no help either. */
      status: 'loading' | 'ready' | 'failed';
      rows: readonly RegistryRow[];
      /** The registry answered, but nothing it lists matches what was typed. */
      noMatch: boolean;
      /** What stands in for the registry when it failed: the phone list's own wallets. */
      fallback: readonly PhoneRow[];
      uri: string | null;
    }
  /** Waiting for the app: with its link to open it again, or without one for a wallet reached through its SDK. */
  | { kind: 'waiting'; wallet: PhoneRow; link: string | null }
  | { kind: 'failed'; message: string; install: InstallLink | null };

/**
 * The one thing the window shows for a state. A refusal shows over everything: it is the answer
 * to whatever was being tried, and `Try again` starts that over. On a phone the list is the home
 * state, "All wallets" and the tapped wallet's waiting line follow it; on a computer the list of
 * extensions is, with the code behind its phone row — or in front, when there is no extension to
 * list.
 */
export const windowView = (state: WindowState): WindowView => {
  if (state.error) {
    return { kind: 'failed', message: state.error, install: state.install };
  }

  if (state.layout === 'phone') {
    if (state.step === 'all') {
      return allWalletsView(state);
    }

    if (state.step === 'waiting' && state.chosen?.kind === 'sdk') {
      return { kind: 'waiting', wallet: state.chosen, link: null };
    }

    if (state.step === 'waiting' && state.chosen?.kind === 'link' && state.uri) {
      return {
        kind: 'waiting',
        wallet: state.chosen,
        link: walletLink(state.chosen.universalLink, state.uri),
      };
    }

    return { kind: 'phone', wallets: state.wallets, uri: state.uri, allWallets: state.canPair };
  }

  if (state.step === 'qr' || state.detected.length === 0) {
    return { kind: 'qr', uri: state.uri, noBrowserWallet: state.detected.length === 0 };
  }

  return { kind: 'desktop', detected: state.detected, phoneRow: state.canPair };
};

const allWalletsView = (state: WindowState): WindowView => {
  const { registry, query, uri } = state;

  if (registry.status === 'ready' && registry.wallets.length > 0) {
    const rows = registryRows(
      filterByName(registry.wallets, query),
      state.wallets,
      uri,
      state.platform
    );

    return {
      kind: 'all',
      query,
      status: 'ready',
      rows,
      noMatch: rows.length === 0,
      fallback: [],
      uri,
    };
  }

  if (registry.status === 'loading') {
    return { kind: 'all', query, status: 'loading', rows: [], noMatch: false, fallback: [], uri };
  }

  // Failed, or an answer with no wallet in it: the person still has the four, and a way to ask again.
  return {
    kind: 'all',
    query,
    status: 'failed',
    rows: [],
    noMatch: false,
    fallback: state.wallets,
    uri,
  };
};
